import { useState, useMemo, useEffect, useRef } from "react";
import {
  Trash2,
  Pencil,
  Check,
  MoreVertical,
  RefreshCw,
  Loader2,
  Copy,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { toast } from "@/hooks/use-toast";
import { SearchTermSet, SearchTermOrigin, SearchTermStatus } from "@/types/search-term";
import { SearchTermOriginsModal } from "../SearchTermOriginsModal";
import { AiReviewModal } from "../AiReviewModal";
import {
  useDeleteSearchTermSet,
  useUpdateSearchTermSet,
  useRefreshSearchTermSet,
  useRegenerateSearchTermSlots,
} from "@/resources/search-term/search-term.api";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { ValidationPanel } from "@/components/ui/ValidationPanel";
import { SearchTermEntry } from "./SearchTermEntry";
import { AddProhibitedKeywordDialog } from "../AddProhibitedKeywordDialog";
import { STALE_THRESHOLD_MS } from "../staleThreshold";

interface SearchTermSetCardProps {
  termSet: SearchTermSet;
  productId: number;
  productName: string;
  brandId: number | null;
  isLatest?: boolean;
  isPublic?: boolean;
  // FR-2: `SearchTermsContentV2` passes `!isPublic && !readOnly`; the share view and the
  // brand-wide `/search-terms` page (`SearchTermsProductCard`) leave this false so neither
  // shows a checkbox or a bulk bar. The card still gates on its own `status` and the
  // in-flight/stale derivation below -- this prop only says the screen allows selection at all.
  selectionEnabled?: boolean;
}

function buildOriginMap(origins: SearchTermOrigin[]): Map<string, SearchTermOrigin> {
  const map = new Map<string, SearchTermOrigin>();
  for (const o of origins) {
    map.set(o.keyword.toLowerCase(), o);
  }
  return map;
}

export function SearchTermSetCard({ termSet, productId, productName, brandId, isLatest, isPublic = false, selectionEnabled = false }: SearchTermSetCardProps) {
  const viewerReadOnly = useViewerReadOnly();
  const readOnly = !isPublic && viewerReadOnly;
  // AUT-41 Part 3: double-click a keyword to add it to a prohibited-keyword tier.
  // Not offered in public share view or for read-only viewers.
  const canAddProhibited = !isPublic && !viewerReadOnly;
  const [originsOpen, setOriginsOpen] = useState(false);
  const [aiReviewOpen, setAiReviewOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [prohibitKeyword, setProhibitKeyword] = useState<string | null>(null);
  const [editableTerms, setEditableTerms] = useState<string[]>(
    termSet.entries.map((e) => e.content),
  );
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (!isEditing) {
      setEditableTerms(termSet.entries.map((e) => e.content));
    }
  }, [termSet.entries, isEditing]);

  const deleteMutation = useDeleteSearchTermSet();
  const updateMutation = useUpdateSearchTermSet();
  const refreshMutation = useRefreshSearchTermSet();
  const regenerateMutation = useRegenerateSearchTermSlots();

  // FR-2/FR-10: the ticked slot indices, local to this card and this version -- nothing
  // persisted (`components/search-term.md § What the SPA owns`).
  const [selectedSlots, setSelectedSlots] = useState<Set<number>>(new Set());

  const originMap = useMemo(() => buildOriginMap(termSet.origins), [termSet.origins]);

  const handleTermChange = (idx: number, value: string) => {
    setEditableTerms((prev) => prev.map((t, i) => (i === idx ? value : t)));
  };

  const handleSave = () => {
    const entries = termSet.entries.map((e, idx) => ({
      id: e.id,
      content: editableTerms[idx] ?? e.content,
    }));
    setIsEditing(false);
    updateMutation.mutate({ id: termSet.id, productId, entries });
  };

  const handleToggleEdit = () => {
    if (isEditing) {
      handleSave();
    } else {
      setEditableTerms(termSet.entries.map((e) => e.content));
      setIsEditing(true);
      // Selection is withdrawn for the duration of the edit (see `selectionAvailable`); clear
      // whatever was ticked so the bulk bar cannot reappear with a stale count once the user
      // leaves edit mode.
      setSelectedSlots(new Set());
    }
  };

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: "Copied!", description: `${label} copied to clipboard.` });
  };

  const handleCopyAll = () => {
    const allText = editableTerms.join("\n\n");
    navigator.clipboard.writeText(allText);
    toast({ title: "Copied!", description: "All search terms copied to clipboard." });
  };

  const handleDelete = () => {
    deleteMutation.mutate({ id: termSet.id, productId });
    setDeleteConfirmOpen(false);
  };

  const handleRefresh = () => {
    toast({ title: "Refreshing search terms..." });
    refreshMutation.mutate({ id: termSet.id, productId });
  };

  const isSingle = termSet.type === "single";
  const isRefreshing = termSet.status === SearchTermStatus.Refreshing;

  // FR-7 / FR-9 / FR-10: a regeneration run is claimed on the set row, not modelled as a
  // `status` (`components/search-term.md § The in-progress state is not a status`). `isStale`
  // is the one derived value everything below reads
  // (`components/search-term.md § What the SPA owns, and what it must not invent`).
  const isRegenerationInFlight = termSet.regeneration_slots != null;
  const isStale =
    termSet.regeneration_slots != null &&
    Date.now() - Date.parse(termSet.regeneration_started_at ?? "") >= STALE_THRESHOLD_MS;
  const hasRegenerationFailed = termSet.regeneration_failed_at != null;

  // FR-2/FR-10: checkboxes and the bulk bar exist only on a completed set, only when the
  // screen allows selection, and only while no run is in flight -- unless that run has gone
  // stale, in which case they come back (the resend carries whatever is ticked now, not the
  // old `regeneration_slots`; the server's claim predicate decides whether it is allowed).
  // Edit mode withdraws them too: `editableTerms` is frozen for the duration of an edit, so a
  // regeneration fired from a ticked slot while editing would be silently overwritten by
  // Save once the run lands (see the comment on `isEditing` in SearchTermEntry). Edit and
  // selection are alternative modes on the card, the same posture Edit already has with a
  // run in flight.
  const selectionAvailable =
    selectionEnabled &&
    !isEditing &&
    termSet.status === SearchTermStatus.Completed &&
    (!isRegenerationInFlight || isStale);

  // FR-8: the toast counts are derived on the client, seeded from the mutation's own `202`
  // (already merged into the cache by the time this effect sees `regeneration_slots` turn
  // non-null) or from a poll that observes a run with no snapshot held -- never recomputed
  // from a persisted result (`components/search-term.md § What the SPA owns`).
  const regenerationSnapshotRef = useRef<Map<number, string> | null>(null);

  useEffect(() => {
    const claimedSlots = termSet.regeneration_slots;

    if (claimedSlots != null) {
      if (regenerationSnapshotRef.current == null) {
        const snapshot = new Map<number, string>();
        for (const entry of termSet.entries) {
          if (claimedSlots.includes(entry.slot_index)) {
            snapshot.set(entry.slot_index, entry.content);
          }
        }
        regenerationSnapshotRef.current = snapshot;
      }
      return;
    }

    // `regeneration_slots` is null: a run this card was watching may have just ended.
    const snapshot = regenerationSnapshotRef.current;
    if (snapshot == null) return;
    regenerationSnapshotRef.current = null;
    setSelectedSlots(new Set());

    // Failure takes precedence: a failed run leaves every slot identical, which is
    // indistinguishable from "no new words found" -- `WEB-002`'s failure notice is the one
    // notice for this ending.
    if (termSet.regeneration_failed_at != null) return;

    let changedCount = 0;
    const unchangedSlots: number[] = [];
    for (const [slotIndex, oldContent] of snapshot) {
      const entry = termSet.entries.find((e) => e.slot_index === slotIndex);
      const newContent = entry ? entry.content : oldContent;
      if (newContent !== oldContent) {
        changedCount += 1;
      } else {
        unchangedSlots.push(slotIndex);
      }
    }

    if (changedCount > 0) {
      toast({
        title: `${changedCount} set${changedCount !== 1 ? "s" : ""} regenerated`,
        description: "Signal AI rebuilt the search terms for the selected sets.",
      });
    }

    for (const slotIndex of unchangedSlots) {
      toast({ description: `No new words were found for Set ${slotIndex + 1}.` });
    }
  }, [termSet.regeneration_slots, termSet.regeneration_failed_at, termSet.entries]);

  const handleToggleSlot = (slotIndex: number, checked: boolean) => {
    setSelectedSlots((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(slotIndex);
      } else {
        next.delete(slotIndex);
      }
      return next;
    });
  };

  const handleRegenerate = () => {
    const slot_indices = Array.from(selectedSlots).sort((a, b) => a - b);
    setSelectedSlots(new Set());
    regenerateMutation.mutate({ id: termSet.id, productId, slot_indices });
  };

  const hasFreeSpace = useMemo(() => {
    const MIN_FREE_SPACE = 10;
    const totalFree = termSet.entries.reduce(
      (sum, e) => sum + Math.max(0, e.char_limit - e.content.length),
      0,
    );
    return totalFree >= MIN_FREE_SPACE;
  }, [termSet.entries]);

  return (
    <>
      <div className={`rounded-lg border border-border p-4 space-y-3 ${isLatest ? "bg-card" : "bg-card opacity-80"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isLatest && (
              <Badge variant="outline" className="text-xs">Latest</Badge>
            )}
            {isRefreshing && (
              <Badge variant="outline" className="text-xs flex items-center gap-1 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Refreshing...
              </Badge>
            )}
          </div>
          {!isPublic && (
            <div className="flex items-center gap-2">
              {!readOnly && !isRefreshing && !isRegenerationInFlight ? (
                <Button
                  variant="outline-on-white"
                  size="sm"
                  onClick={handleToggleEdit}
                >
                  {isEditing ? <Check className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                  {isEditing ? "Save" : "Edit"}
                </Button>
              ) : null}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="w-8">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {!readOnly ? (
                    <DropdownMenuItem onClick={() => setAiReviewOpen(true)}>
                      <AISparkles className="mr-2" />
                      AI Review
                    </DropdownMenuItem>
                  ) : null}
                  {!readOnly &&
                  termSet.status === SearchTermStatus.Completed &&
                  hasFreeSpace &&
                  !isRegenerationInFlight ? (
                    <DropdownMenuItem
                      onClick={handleRefresh}
                      disabled={refreshMutation.isPending || isRefreshing}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuItem onClick={handleCopyAll}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy All
                  </DropdownMenuItem>
                  {!readOnly ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => setDeleteConfirmOpen(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        {isStale && (
          <div className="flex items-center gap-1 text-amber-600 dark:text-amber-500 text-xs">
            <AlertCircle className="h-3.5 w-3.5" />
            <span>This is taking longer than expected. You may want to try again.</span>
          </div>
        )}

        {hasRegenerationFailed && (
          <div className="flex items-center gap-2 text-destructive text-xs">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            <span>Regeneration failed. Your search terms were not changed.</span>
          </div>
        )}

        {selectionAvailable && (
          <BulkActionBar
            count={selectedSlots.size}
            onClear={() => setSelectedSlots(new Set())}
            entityLabel="set"
          >
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="text-xs h-7 gap-1">
                  Bulk Actions
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-popover z-50">
                <DropdownMenuItem onClick={handleRegenerate} disabled={regenerateMutation.isPending}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </BulkActionBar>
        )}

        <div className="space-y-2">
          {termSet.entries.map((entry, idx) => (
            <SearchTermEntry
              key={entry.id}
              idx={idx}
              term={editableTerms[idx] ?? entry.content}
              limit={entry.char_limit}
              isSingle={isSingle}
              isEditing={isEditing}
              readOnly={readOnly}
              originMap={originMap}
              isRegenerating={termSet.regeneration_slots?.includes(entry.slot_index) ?? false}
              showCheckbox={selectionAvailable}
              checked={selectedSlots.has(entry.slot_index)}
              onCheckedChange={(checked) => handleToggleSlot(entry.slot_index, checked)}
              onTermChange={handleTermChange}
              onCopy={handleCopy}
              onAddToProhibited={canAddProhibited ? setProhibitKeyword : undefined}
            />
          ))}
        </div>

        <ValidationPanel validation={termSet.validation} />

        {!isEditing && (
          <p className="text-[10px] text-muted-foreground">
            Hover any term to see its origin phrase and search volume.
          </p>
        )}
      </div>

      <SearchTermOriginsModal
        open={originsOpen}
        onOpenChange={setOriginsOpen}
        origins={termSet.origins}
        productName={productName}
      />

      {canAddProhibited && (
        <AddProhibitedKeywordDialog
          open={prohibitKeyword !== null}
          onOpenChange={(o) => {
            if (!o) setProhibitKeyword(null);
          }}
          keyword={prohibitKeyword ?? ""}
          brandId={brandId}
          productId={productId}
          productName={productName}
        />
      )}

      {!readOnly ? (
        <AiReviewModal
          open={aiReviewOpen}
          onOpenChange={setAiReviewOpen}
          termSet={termSet}
          productName={productName}
          brandId={brandId}
        />
      ) : null}

      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete Search Term Set?"
        description="This will permanently delete this search term set. This action cannot be undone."
        onConfirm={handleDelete}
      />
    </>
  );
}
